import { z } from "zod";
import { BillingModeSchema, EventKindSchema } from "./common";
import { EventFeaturesSchema, EventFeatureKeySchema } from "./event-features";
import { EventProgressSchema } from "./event-progress";

export const EventSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.enum(["draft", "confirmed", "completed", "cancelled"]),
  eventDate: z.coerce.date(),
  /** Phase 22.1 — เลื่อนแล้วยังไม่มีวันใหม่ */
  dateTbd: z.boolean().default(false),
  endDate: z.coerce.date().nullable(),
  durationMinutes: z.number().nullable(),
  locationText: z.string().nullable(),
  locationProvince: z.string().nullable(),
  isProvincial: z.boolean(),
  perDiemPolicyId: z.string().nullable(),
  perDiemDays: z.number().int().nullable(),
  eventKind: EventKindSchema,
  billingMode: BillingModeSchema,
  categoryId: z.string().nullable(),
  eventFeatures: EventFeaturesSchema,
  categoryName: z.string().nullable().optional(),
  categorySlug: EventKindSchema.nullable().optional(),
  roomId: z.string().nullable(),
  roomName: z.string().nullable().optional(),
  requestId: z.string().nullable(),
  priorityOrder: z.number().nullable(),
  notes: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  /** Phase 20 — derived operational / AR / AP progress */
  progress: EventProgressSchema.optional(),
  /** Phase 22 — จำนวนครั้งที่เคยเลื่อนวัน (list/detail enrich) */
  postponeCount: z.number().int().optional(),
  wasPostponed: z.boolean().optional(),
  /** เหลือ ≤9 วัน แต่ยังไม่จัดทีมนักแสดง */
  teamAssignmentWarning: z.boolean().optional(),
});
export type EventDTO = z.infer<typeof EventSchema>;

const perDiemRefine = (
  vals: {
    isProvincial?: boolean;
    perDiemDays?: number | null;
  },
  ctx: z.RefinementCtx,
) => {
  if (vals.isProvincial && vals.perDiemDays === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["perDiemDays"],
      message: "ຈຳນວນວັນ per diem ຕ້ອງ ≥ 1",
    });
  }
};

export const CreateEventSchema = z
  .object({
    title: z.string().min(1, "ຕ້ອງປ້ອນຊື່ງານ"),
    status: z
      .enum(["draft", "confirmed", "completed", "cancelled"])
      .default("draft"),
    eventDate: z.string().min(1, "ຕ້ອງເລືອກວັນທີ"),
    endDate: z.string().nullable().optional(),
    durationMinutes: z.number().int().min(1).nullable().optional(),
    locationText: z.string().nullable().optional(),
    locationProvince: z.string().nullable().optional(),
    isProvincial: z.boolean().default(false),
    perDiemPolicyId: z.string().nullable().optional(),
    perDiemDays: z.number().int().min(1).nullable().optional(),
    eventKind: EventKindSchema.default("stage_show"),
    billingMode: BillingModeSchema.default("performance"),
    categoryId: z.string().nullable().optional(),
    eventFeatures: EventFeaturesSchema.optional(),
    roomId: z.string().nullable().optional(),
    priorityOrder: z.number().int().nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .superRefine(perDiemRefine);
export type CreateEventDTO = z.infer<typeof CreateEventSchema>;

export const UpdateEventSchema = z
  .object({
    title: z.string().min(1, "ຕ້ອງປ້ອນຊື່ງານ").optional(),
    status: z.enum(["draft", "confirmed", "completed", "cancelled"]).optional(),
    eventDate: z.string().optional(),
    endDate: z.string().nullable().optional(),
    durationMinutes: z.number().int().min(1).nullable().optional(),
    locationText: z.string().nullable().optional(),
    locationProvince: z.string().nullable().optional(),
    isProvincial: z.boolean().optional(),
    perDiemPolicyId: z.string().nullable().optional(),
    perDiemDays: z.number().int().min(1).nullable().optional(),
    eventKind: EventKindSchema.optional(),
    billingMode: BillingModeSchema.optional(),
    categoryId: z.string().nullable().optional(),
    eventFeatures: EventFeaturesSchema.optional(),
    roomId: z.string().nullable().optional(),
    priorityOrder: z.number().int().nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .superRefine(perDiemRefine);

export type UpdateEventDTO = z.infer<typeof UpdateEventSchema>;

export const PostponeEventSchema = z
  .object({
    /** เลื่อนแบบยังไม่มีวันใหม่ */
    indefinite: z.boolean().optional(),
    newEventDate: z.string().nullable().optional(),
    newEndDate: z.string().nullable().optional(),
    reason: z
      .string()
      .trim()
      .min(1, "ຕ້ອງປ້ອນເຫດຜົນ")
      .max(500, "ເຫດຜົນຍາວເກີນໄປ"),
  })
  .superRefine((value, ctx) => {
    if (value.indefinite) return;
    if (!value.newEventDate?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["newEventDate"],
        message: "ຕ້ອງເລືອກວັນທີໃໝ່ ຫຼື ເລືອກເລື່ອນບໍ່ມີກຳນົດ",
      });
    }
  });
export type PostponeEventDTO = z.infer<typeof PostponeEventSchema>;

/** Lightweight payload for edit form (no assignments/progress). */
export const EventEditFormSchema = EventSchema.extend({
  lockedFeatures: z.array(EventFeatureKeySchema),
}).omit({ progress: true, postponeCount: true, wasPostponed: true });
export type EventEditFormDTO = z.infer<typeof EventEditFormSchema>;

export const EventDateChangeSchema = z.object({
  id: z.string(),
  eventId: z.string(),
  previousEventDate: z.coerce.date(),
  previousEndDate: z.coerce.date().nullable(),
  newEventDate: z.coerce.date().nullable(),
  newEndDate: z.coerce.date().nullable(),
  indefinite: z.boolean().default(false),
  reason: z.string(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type EventDateChangeDTO = z.infer<typeof EventDateChangeSchema>;

export const PostponeEventResultSchema = z.object({
  event: EventSchema,
  change: EventDateChangeSchema,
});
export type PostponeEventResultDTO = z.infer<typeof PostponeEventResultSchema>;
